import * as React from 'react';
import { X } from 'lucide-react';
import { Button } from './Button';
import { cn } from '../cn';

export interface ToastItem { id: number; title: React.ReactNode; description?: React.ReactNode; tone?: 'neutral' | 'ok' | 'danger'; undo?: { label: React.ReactNode; onUndo: () => void | Promise<void>; ms?: number }; }
interface Ctx { push: (t: Omit<ToastItem, 'id'>) => number; dismiss: (id: number) => void; }
const ToastCtx = React.createContext<Ctx | null>(null);

/** Toasts with a 10 s undo (foundation spec "Confirmation and undo"): the write is reverted through the API by `onUndo`. */
export function ToastProvider({ children, closeLabel }: { children: React.ReactNode; closeLabel: string }) {
  const [items, setItems] = React.useState<ToastItem[]>([]);
  const seq = React.useRef(0);
  const dismiss = React.useCallback((id: number) => setItems((s) => s.filter((t) => t.id !== id)), []);
  const push = React.useCallback((t: Omit<ToastItem, 'id'>) => {
    const id = ++seq.current;
    setItems((s) => [...s, { ...t, id }]);
    window.setTimeout(() => dismiss(id), t.undo?.ms ?? (t.undo ? 10_000 : 5_000));
    return id;
  }, [dismiss]);
  const value = React.useMemo(() => ({ push, dismiss }), [push, dismiss]);
  return (
    <ToastCtx.Provider value={value}>
      {children}
      <div aria-live="polite" aria-relevant="additions" className="pointer-events-none fixed bottom-4 z-50 flex w-full flex-col items-end gap-2 px-4" style={{ insetInlineEnd: 0 }}>
        {items.map((t) => (
          <div key={t.id} role="status" className={cn('pointer-events-auto flex w-[min(92vw,24rem)] items-start gap-3 rounded-md border bg-surface p-3 shadow-pop', t.tone === 'danger' ? 'border-danger/50' : t.tone === 'ok' ? 'border-ok/50' : 'border-border')}>
            <div className="flex-1">
              <div className="text-sm font-medium">{t.title}</div>
              {t.description ? <div className="text-xs text-fg-muted">{t.description}</div> : null}
            </div>
            {t.undo ? <Button variant="link" size="sm" onClick={async () => { await t.undo!.onUndo(); dismiss(t.id); }}>{t.undo.label}</Button> : null}
            <Button variant="ghost" size="icon" aria-label={closeLabel} onClick={() => dismiss(t.id)}><X className="size-4" aria-hidden /></Button>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
export function useToast(): Ctx {
  const c = React.useContext(ToastCtx);
  if (!c) throw new Error('useToast outside ToastProvider');
  return c;
}
